import React, { useState } from 'react';
import { Sparkline, StatusPill, SecondaryBtn, PrimaryBtn } from '../systemKit';
import { describeAlert, formatDetectionAge } from '../../lib/shrinkflation';
import { describeNeedsReview } from '../../lib/parseGuardrails';

// VolumeRow — Price Tracker's Volume-view row. Mirrors ItemRow's chip-expansion
// behavior so users get the same plain-language description on click whether
// they're looking at Price or Volume. Local row state for chip expansion;
// resets naturally on filter / sort because the row unmounts.
//
// Per DESIGN_SYSTEM.md §Inline chip expansion.

const SHRINKFLATION_ICON = (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <path d="M12 9v4"/>
    <path d="M12 17h.01"/>
  </svg>
);
const NEEDS_REVIEW_ICON = (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-4"/>
    <path d="M12 8h.01"/>
  </svg>
);

function ExpandableChip({ icon, label, expanded, onToggle, title }) {
  return (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggle?.(); } }}
      className="inline-flex items-center"
      style={{ cursor: 'pointer' }}
    >
      <StatusPill tone="amber">
        <span className="inline-flex items-center gap-1">
          {icon}
          {label}
          <svg
            width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(180deg)' : 'none' }}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </StatusPill>
    </span>
  );
}

export default function VolumeRow({
  item,
  vol,                  // { curr: {qty,lbs}, prior: {qty,lbs}, sparkline: [...] }
  useWeight,
  alert,                // active shrinkflation alert (if any)
  needsReviewItem,      // food_items row record when flagged (or null)
  onAlertView, onAlertDismiss,
  onNeedsReviewReview, onNeedsReviewAccept,
  onOpenDrawer,         // fires when the row body (NOT a chip) is clicked → parent opens VolumeDetailDrawer
}) {
  const [chipsOpen, setChipsOpen] = useState({ shrinkflation: false, needsReview: false });

  const unit = useWeight ? 'lbs' : 'cs';
  const currValue = useWeight ? vol.curr.lbs : vol.curr.qty;
  const priorValue = useWeight ? vol.prior.lbs : vol.prior.qty;
  const pctChange = priorValue > 0 ? ((currValue - priorValue) / priorValue) * 100 : null;
  const deltaColor = pctChange == null ? 'var(--text-muted)' : (pctChange >= 0 ? 'var(--text-success)' : 'var(--text-critical)');
  const sparkData = vol.sparkline.map(p => p.value).filter(v => v > 0);

  return (
    <>
      <tr
        className="border-b border-gray-50 transition-colors"
        data-food-item-id={item.id}
        onClick={onOpenDrawer ? () => onOpenDrawer() : undefined}
        style={{ cursor: onOpenDrawer ? 'pointer' : 'default' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <td className="px-4 py-3 font-medium text-gray-900">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span>{item.normalized_name}</span>
            {needsReviewItem && (
              <ExpandableChip
                icon={NEEDS_REVIEW_ICON}
                label="Needs review"
                expanded={chipsOpen.needsReview}
                onToggle={() => setChipsOpen(s => ({ ...s, needsReview: !s.needsReview }))}
                title="This item's pack/weight data looks suspicious. Click to expand."
              />
            )}
            {alert && (
              <ExpandableChip
                icon={SHRINKFLATION_ICON}
                label="Shrinkflation"
                expanded={chipsOpen.shrinkflation}
                onToggle={() => setChipsOpen(s => ({ ...s, shrinkflation: !s.shrinkflation }))}
                title={`${describeAlert(alert)}. Click to expand.`}
              />
            )}
            {item.is_catch_weight && <span className="text-gray-400" style={{ fontSize: 10 }}>catch-weight</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          {item.category && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{item.category}</span>
          )}
        </td>
        <td className="px-4 py-3 text-right font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {currValue > 0 ? `${currValue.toLocaleString('en-US', { maximumFractionDigits: useWeight ? 0 : 1 })} ${unit}` : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
        </td>
        <td className="px-4 py-3 text-right text-xs" style={{ color: deltaColor, fontVariantNumeric: 'tabular-nums' }}>
          {pctChange == null ? '\u2014' : `${pctChange >= 0 ? '\u2191' : '\u2193'} ${Math.abs(pctChange).toFixed(0)}%`}
        </td>
        <td className="px-4 py-3">
          <Sparkline data={sparkData} tone="volume" />
        </td>
      </tr>

      {chipsOpen.needsReview && needsReviewItem && (() => {
        const description = describeNeedsReview(needsReviewItem);
        if (!description) return null;
        const isHigh = description.confidence === 'high';
        return (
          <tr>
            <td colSpan={5} className="px-0 py-0">
              <div className="px-4 py-3" style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
                <div className="text-xs font-semibold uppercase mb-1" style={{ letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
                  Needs review
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{description.problem}</div>
                <div className="text-xs italic mt-0.5" style={{ color: 'var(--text-muted)' }}>{description.trace}</div>
                <div className="flex items-center justify-end gap-2 mt-2">
                  <SecondaryBtn onClick={() => onNeedsReviewReview?.(needsReviewItem)} className="!px-3 !py-1.5 !text-xs">Review</SecondaryBtn>
                  {isHigh && onNeedsReviewAccept && (
                    <PrimaryBtn onClick={() => onNeedsReviewAccept(needsReviewItem)} className="!px-3 !py-1.5 !text-xs">Accept fix</PrimaryBtn>
                  )}
                </div>
              </div>
            </td>
          </tr>
        );
      })()}

      {chipsOpen.shrinkflation && alert && (
        <tr>
          <td colSpan={5} className="px-0 py-0">
            <div className="px-4 py-3" style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
              <div className="text-xs font-semibold uppercase mb-1" style={{ letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
                Shrinkflation
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{describeAlert(alert)}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Detected {formatDetectionAge(alert.first_detected_at)}{item.vendor_name ? ` \u00B7 ${String(item.vendor_name).replace(/\n/g, ' \u00B7 ')}` : ''}
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                <SecondaryBtn onClick={() => onAlertView?.(alert)} className="!px-3 !py-1.5 !text-xs">View item</SecondaryBtn>
                <button
                  onClick={() => onAlertDismiss?.(alert)}
                  className="text-xs px-3 py-1.5 rounded-lg transition-colors"
                  style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
